import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, EASE } from "@/components/Reveal";

const HERO_IMAGES = [
  { src: "/images/campaign-2.webp", alt: "Woman wearing the black SHUTUP T-shirt with a surreal red mask" },
  { src: "/images/people-rebel-2.webp", alt: "Person in a tiger mask wearing the black SHUTUP T-shirt at a fairground" },
  { src: "/images/people-rebel-1.webp", alt: "Elder rebel wearing the black SHUTUP T-shirt with the SHUTUP poster on a brick wall" },
];

export default function Hero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const [frame, setFrame] = useState(() => Math.floor(Math.random() * HERO_IMAGES.length));
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    const id = setInterval(() => setFrame((f) => (f + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] overflow-hidden bg-black">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <AnimatePresence>
          <motion.img
            key={frame}
            src={HERO_IMAGES[frame].src}
            alt={HERO_IMAGES[frame].alt}
            data-testid="hero-image"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: EASE }}
            className="absolute inset-0 h-full w-full object-cover object-[50%_28%]"
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-black/40" />

      <motion.div
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.8, ease: EASE }}
        className="absolute top-16 sm:top-[4.5rem] inset-x-0 z-20 -rotate-1"
        data-testid="hero-company-banner"
      >
        <div className="bg-acid border-y-2 border-black py-2 sm:py-2.5 text-center shadow-[0_4px_0_rgba(0,0,0,0.6)]">
          <p className="font-display text-black text-sm sm:text-lg tracking-[0.3em]">THE SHUTUP COMPANY</p>
        </div>
      </motion.div>

      <div className="absolute bottom-24 right-5 sm:right-10 z-20 flex gap-2" data-testid="hero-frame-dots">
        {HERO_IMAGES.map((_, i) => (
          <span
            key={i}
            className={`h-1 transition-all duration-500 ${i === frame ? "w-8 bg-acid" : "w-4 bg-paper/40"}`}
          />
        ))}
      </div>

      <div className="absolute inset-x-0 bottom-0 px-5 sm:px-10 pb-8 sm:pb-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
          className="font-sans text-paper/80 text-[11px] sm:text-xs font-semibold tracking-[0.35em] mb-4"
          data-testid="hero-eyebrow"
        >
          LIMITED EDITION T-SHIRT — 200 PIECES ONLY — ₹2,000
        </motion.p>

        <h1 className="font-display leading-[0.85] text-paper" data-testid="hero-headline" data-sound="guitar" data-cursor="shutup">
          <MaskedLine delay={0.25} className="text-[clamp(4.5rem,19vw,17rem)] tracking-tight">
            SHUTUP.
          </MaskedLine>
          <MaskedLine delay={0.45} className="text-[clamp(2.2rem,8.5vw,7.5rem)]">
            <span className="stroke-pink">&amp; SPEAK</span> <span className="text-acid">WILD.</span>
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
          className="mt-7 flex flex-col items-start gap-4"
        >
          <button
            data-testid="hero-buy-button"
            data-cursor="shutup"
            onClick={() => navigate("/product")}
            className="group font-sans bg-paper text-black font-bold tracking-[0.12em] text-sm sm:text-base px-8 py-4 hover:bg-pink transition-colors"
          >
            BUY THE SHUTUP T-SHIRT{" "}
            <span className="inline-block transition-transform group-hover:translate-x-1.5">→</span>
          </button>
          <p className="font-sans text-paper/60 text-[11px] sm:text-xs tracking-[0.25em]" data-testid="hero-specs">
            BLACK / OFF WHITE · OVERSIZED · XS–XXL
          </p>
        </motion.div>
      </div>
    </section>
  );
}
